import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
	ProductCategoryToken,
	CategoryManufacturerMapToken,
	CategoryNewsEntryMapToken,
	CategoryAttributesMapToken
} from '@sleed/bizcommerce-api';
import { categoriesApi } from '~/lib/api/client';

/**
 * Per-category relation lists (featured products / manufacturers / news / attributes) shown in the
 * tree's row-action modals. Mirrors the legacy Categories sub-grids: read the related list, toggle
 * isFeatured/hide/sort, and (manufacturers/news) add via an entity picker + remove. Updates post the
 * map token `{ id, si, isFeatured }`; the server resolves the row by id (CategoriesController.Update*).
 */
export const categoryRelationKeys = {
	products: (cid: number) => ['category-relations', 'products', cid] as const,
	manufacturers: (cid: number) => ['category-relations', 'manufacturers', cid] as const,
	news: (cid: number) => ['category-relations', 'news', cid] as const,
	attributes: (cid: number) => ['category-relations', 'attributes', cid] as const
};

/* ----------------------------------------------------------------- Products (featured toggle only) */
export function useCategoryProducts(cid: number | null) {
	return useQuery<ProductCategoryToken[]>({
		queryKey: categoryRelationKeys.products(cid ?? 0),
		queryFn: async () => (await categoriesApi.getProductsByCategoryId(cid!)).data.items ?? [],
		enabled: cid != null && cid > 0
	});
}
export function useCategoryProductMutations(cid: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: categoryRelationKeys.products(cid) });
	const update = useMutation({
		mutationFn: async (token: ProductCategoryToken) => (await categoriesApi.updateFeatured(token)).data,
		onSuccess: invalidate
	});
	return { update };
}

/* --------------------------------------------------------- Manufacturers (toggle + sort, add, remove) */
export function useCategoryManufacturers(cid: number | null) {
	return useQuery<CategoryManufacturerMapToken[]>({
		queryKey: categoryRelationKeys.manufacturers(cid ?? 0),
		queryFn: async () => (await categoriesApi.getManufacturersByCategoryId(cid!)).data.items ?? [],
		enabled: cid != null && cid > 0
	});
}
export function useCategoryManufacturerMutations(cid: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: categoryRelationKeys.manufacturers(cid) });
	// Update reuses the ProductCategoryToken shape the server binds for the manufacturer map row.
	const update = useMutation({
		mutationFn: async (token: ProductCategoryToken) => (await categoriesApi.updateFeaturedManufacturers(token)).data,
		onSuccess: invalidate
	});
	const add = useMutation({
		mutationFn: async (manufacturerId: number) =>
			(await categoriesApi.insertFeaturedManufacturers({ cid, mfid: manufacturerId } as CategoryManufacturerMapToken)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await categoriesApi.deleteFeaturedManufacturers(id)).data,
		onSuccess: invalidate
	});
	return { update, add, remove };
}

/* ----------------------------------------------------------------- News (toggle + sort, add, remove) */
export function useCategoryNews(cid: number | null) {
	return useQuery<CategoryNewsEntryMapToken[]>({
		queryKey: categoryRelationKeys.news(cid ?? 0),
		queryFn: async () => (await categoriesApi.getNewsEntriesByCategoryId(cid!)).data.items ?? [],
		enabled: cid != null && cid > 0
	});
}
export function useCategoryNewsMutations(cid: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: categoryRelationKeys.news(cid) });
	const update = useMutation({
		mutationFn: async (token: CategoryNewsEntryMapToken) => (await categoriesApi.updateFeaturedNewsEntries(token)).data,
		onSuccess: invalidate
	});
	const add = useMutation({
		mutationFn: async (newsEntryId: number) =>
			(await categoriesApi.insertFeaturedNewsEntries({ cid, neid: newsEntryId } as CategoryNewsEntryMapToken)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await categoriesApi.deleteFeaturedNewsEntries(id)).data,
		onSuccess: invalidate
	});
	return { update, add, remove };
}

/* ------------------------------------------------------------- Attributes (toggle hide + isFeatured) */
export function useCategoryAttributes(cid: number | null) {
	return useQuery<CategoryAttributesMapToken[]>({
		queryKey: categoryRelationKeys.attributes(cid ?? 0),
		queryFn: async () => (await categoriesApi.getAttributesByCategoryId(cid!)).data.items ?? [],
		enabled: cid != null && cid > 0
	});
}
export function useCategoryAttributeMutations(cid: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: categoryRelationKeys.attributes(cid) });
	const update = useMutation({
		mutationFn: async (token: CategoryAttributesMapToken) => (await categoriesApi.updateFeaturedAttributes(token)).data,
		onSuccess: invalidate
	});
	return { update };
}
